import { useEffect, useState } from "react";
import type { DashboardData } from "../types";
import { api, ApiError } from "../api";
import ErrorState from "./ErrorState";
import { LineChart, BarChart, PieChart } from "./charts";
import {
  IconUsers,
  IconUserPlus,
  IconChat,
  IconUserMinus,
  IconTrendUp,
  IconCap,
  IconPulse,
  IconStar,
  IconRefresh,
} from "../icons";

const COLORS = ["#0c7a3f", "#15a85a", "#34d27e", "#0a5c30", "#5cd99a", "#1f7a4d", "#8be0b3"];

export default function Dashboard({ refreshKey }: { refreshKey: number }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .dashboard()
      .then((d) => alive && setData(d))
      .catch((err) => {
        if (!alive) return;
        setError(err instanceof ApiError ? err : new ApiError(String(err)));
      });
    return () => {
      alive = false;
    };
  }, [refreshKey, reload]);

  if (error)
    return (
      <ErrorState
        message={error.message}
        code={error.code}
        onRetry={() => setReload((n) => n + 1)}
      />
    );
  if (!data) return <div className="empty">Carregando indicadores...</div>;

  const c = data.cards;
  const cards = [
    { label: "Colaboradores ativos", value: c.colaboradores, Icon: IconUsers, tone: "green" },
    { label: "Contratações", value: c.contratacoes, Icon: IconUserPlus, tone: "emerald" },
    { label: "Entrevistas", value: c.entrevistas, Icon: IconChat, tone: "teal" },
    { label: "Demissões", value: c.demissoes, Icon: IconUserMinus, tone: "red" },
    { label: "Promoções", value: c.promocoes, Icon: IconTrendUp, tone: "emerald" },
    { label: "Treinamentos", value: c.treinamentos, Icon: IconCap, tone: "lime" },
    { label: "Clima organizacional (média)", value: c.engajamento_medio, Icon: IconPulse, tone: "teal" },
    { label: "Avaliação média", value: c.avaliacao_media, Icon: IconStar, tone: "amber" },
    { label: "Turnover", value: `${c.turnover}%`, Icon: IconRefresh, tone: "red" },
  ];

  return (
    <div className="dashboard">
      <div className="cards">
        {cards.map((card) => (
          <div className={`card tone-${card.tone}`} key={card.label}>
            <div className="card-icon">
              <card.Icon size={22} />
            </div>
            <div className="card-body">
              <span className="card-value">{card.value}</span>
              <span className="card-label">{card.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="charts">
        <ChartBox title="Contratações vs. Demissões (12 meses)">
          <LineChart
            data={data.contratacoesVsDemissoes}
            xKey="mes"
            series={[
              { key: "contratacoes", label: "Contratações", color: "#0c7a3f" },
              { key: "demissoes", label: "Demissões", color: "#ef4444" },
            ]}
          />
        </ChartBox>

        <ChartBox title="Headcount por departamento">
          <PieChart
            data={data.headcount}
            valueKey="total"
            labelKey="departamento"
            colors={COLORS}
          />
        </ChartBox>

        <ChartBox title="Treinamentos por mês">
          <BarChart
            data={data.treinamentosPorMes}
            xKey="mes"
            series={[
              { key: "total", label: "Treinamentos", color: "#0c7a3f" },
              { key: "horas", label: "Horas", color: "#34d27e" },
            ]}
          />
        </ChartBox>

        <ChartBox title="Pesquisa de clima por período">
          <BarChart
            data={data.engajamentoPorPeriodo}
            xKey="periodo"
            max={100}
            series={[{ key: "media", label: "Pontuação média", color: "#15a85a" }]}
          />
        </ChartBox>
      </div>
    </div>
  );
}

function ChartBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="chart-box">
      <h3>{title}</h3>
      {children}
    </div>
  );
}
